export function buildPublishedWebsiteUrl(
  apiUrl: string,
  subdomain: string,
  publicHandle?: string | null,
  path = '/'
): string {
  const apiOrigin = new URL(apiUrl);
  const normalizedPath = normalizePublishedPath(path);

  if (isLocalApiOrigin(apiOrigin.hostname)) {
    return new URL(`/sites/${subdomain}${normalizedPath === '/' ? '' : normalizedPath}`, apiOrigin.origin).toString();
  }

  const publishedHost = buildPublishedWebsiteHost(apiUrl, subdomain, publicHandle);
  if (publishedHost) {
    return `${apiOrigin.protocol}//${publishedHost}${normalizedPath}`;
  }

  return new URL(`/sites/${subdomain}${normalizedPath === '/' ? '' : normalizedPath}`, apiOrigin.origin).toString();
}

export function buildPublishedWebsiteHost(
  apiUrl: string,
  subdomain: string,
  publicHandle?: string | null
): string | null {
  const apiOrigin = new URL(apiUrl);
  if (isLocalApiOrigin(apiOrigin.hostname)) {
    return null;
  }

  const normalizedSubdomain = normalizeHostLabel(subdomain);
  const normalizedHandle = normalizeHostLabel(publicHandle ?? '');
  if (!normalizedSubdomain || !normalizedHandle) {
    return null;
  }

  if (apiOrigin.hostname.startsWith('api.')) {
    return buildCombinedHostLabel(normalizedSubdomain, normalizedHandle, apiOrigin.hostname.slice(4));
  }

  if (apiOrigin.hostname.includes('-api.')) {
    return buildCombinedHostLabel(normalizedSubdomain, normalizedHandle, 'bizzweaver.com');
  }

  return null;
}

function isLocalApiOrigin(hostname: string): boolean {
  return hostname === 'localhost' || hostname === '127.0.0.1';
}

function normalizeHostLabel(value: string): string | null {
  const normalized = value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9-]+/g, '-')
    .replace(/-{2,}/g, '-')
    .replace(/^-+|-+$/g, '');

  return normalized.length > 0 ? normalized : null;
}

function buildCombinedHostLabel(subdomain: string, publicHandle: string, rootDomain: string): string | null {
  const separator = '-';
  const maxLabelLength = 63;
  const availableSubdomainLength = maxLabelLength - publicHandle.length - separator.length;

  if (availableSubdomainLength < 3) {
    return null;
  }

  const trimmedSubdomain = subdomain.slice(0, availableSubdomainLength).replace(/-+$/g, '');
  if (!trimmedSubdomain) {
    return null;
  }

  return `${trimmedSubdomain}${separator}${publicHandle}${separator}sites.${rootDomain}`;
}

function normalizePublishedPath(path: string): string {
  if (!path || path === '/') {
    return '/';
  }

  const cleanPath = path.trim().split('?')[0]?.split('#')[0] || '/';
  const prefixed = cleanPath.startsWith('/') ? cleanPath : `/${cleanPath}`;
  return prefixed.replace(/\/{2,}/g, '/').replace(/\/+$/, '') || '/';
}
